import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { useUserAuth } from "../contexts/UserAuthContext";
import { MaintenanceNotice, useProductPaused } from "./MaintenanceNotice";

/**
 * Renders children only if the signed-in user has the required entitlement.
 * If signed out → redirects to /auth/login with a return-to path.
 * If signed in but missing entitlement → renders an upsell card.
 *
 * Used to wrap the legacy hidden post-checkout pages so they live behind
 * clean /my-account/* URLs instead of obscure secret URLs.
 */
export default function MyAccountGate({ entitlement, productLabel, children, returnPath }) {
  const { user, loading, hasEntitlement } = useUserAuth();
  const { loading: availLoading, paused, message } = useProductPaused(entitlement);
  const nav = useNavigate();
  const next = returnPath || (typeof window !== "undefined" ? window.location.pathname : "/my-account");

  useEffect(() => {
    if (!loading && !user) {
      nav(`/auth/login?next=${encodeURIComponent(next)}`, { replace: true });
    }
  }, [user, loading, nav, next]);

  if (loading || !user || availLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="gate-loading">
        <Loader2 className="w-10 h-10 animate-spin text-[#E63B2E]" />
      </div>
    );
  }

  if (paused) {
    return <MaintenanceNotice productLabel={productLabel} message={message} />;
  }

  if (!hasEntitlement(entitlement)) {
    return (
      <section className="bg-slate-50 py-16 md:py-24 min-h-[60vh]">
        <div className="max-w-2xl mx-auto px-5 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#E63B2E]/10 text-[#E63B2E] mb-5">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#0F1B2E] mb-3">
            {productLabel} is locked
          </h1>
          <p className="text-slate-600 mb-8">
            Your account doesn't yet have access to this product. If you've just
            paid, the receipt email contains a one-click sign-in link that
            unlocks it automatically.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/cv"
              className="rounded-full bg-[#E63B2E] hover:bg-[#c92f24] text-white font-bold px-6 py-3 text-sm"
              data-testid="gate-browse-products"
            >
              Browse products
            </Link>
            <Link
              to="/my-account"
              className="rounded-full bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 font-bold px-6 py-3 text-sm inline-flex items-center gap-1.5"
              data-testid="gate-back-to-account"
            >
              <ArrowLeft className="w-4 h-4" /> Back to my account
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <div className="bg-slate-100 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-3 flex items-center justify-between text-sm">
          <Link
            to="/my-account"
            className="text-slate-600 hover:text-[#E63B2E] inline-flex items-center gap-1.5 font-semibold"
            data-testid="gate-breadcrumb-back"
          >
            <ArrowLeft className="w-4 h-4" /> My account
          </Link>
          <span className="text-slate-500 text-xs hidden sm:inline">{productLabel}</span>
        </div>
      </div>
      {children}
    </>
  );
}
